import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch, apiUploadFile } from "@/lib/api";
import type {
  AgganciaCommessaBody,
  CalendarioResponse,
  CaricoPrevistoDetail,
  CaricoPrevistoLista,
  PartenzeGiorno,
  CaricoPrevistoPatch,
  DocumentiKpiResponse,
  DocumentiUploadResponse,
  DocCompletamento,
  DocCompletamentoBody,
  FiltroPacchetti,
  GeneratoBody,
  ManifestBody,
  PaccoDetail,
  PacchettiResponse,
  RigeneraEsito,
  TemplateDetail,
  TemplatePanoramica,
  TemplateRead,
  TemplateTipo,
  AgenteMemoria,
  AgenteWhitelistRow,
  AnnullaAnticipoBody,
  AnnullaResiduoResponse,
  ApplicaProposteBody,
  ApplicaProposteResponse,
  AssegnazioniEditBody,
  AvvisiResponse,
  AvvisoRitiroUpdate,
  ClienteEmailResponse,
  ClienteEmailUpsert,
  PropostaResolve,
  CambiaStatoCaricoBody,
  CarichiPipelineResponse,
  CaricoDettaglio,
  ChecklistDocumentiResponse,
  CommesseAperteResponse,
  ConversazioneCarico,
  CreaCaricoBody,
  LavoroResponse,
  CreaCommessaLogisticaBody,
  EmailInbound,
  FascicoloFile,
  GeneraDocumentiBody,
  ImportFoglioResponse,
  ImpostazioniGlobali,
  ImpostazioniGlobaliUpdate,
  PipelineTipo,
  Paese,
  PaeseUpsert,
  PreferenzaCliente,
  PreferenzaClienteUpsert,
  Bozza,
  ProponiCarichiResponse,
  PronteMagazzinoResponse,
  RaggruppamentoProposto,
  SetDocumentiRow,
  StatoDocumenti,
  StatoLogisticoResponse,
  StatoPipeline,
  UscitePalletResponse,
} from "@/types/api";

// ── M3 v2: Pipeline logistica end-to-end ──────────────────────────

export interface UsePipelineOptions {
  stato?: StatoPipeline[];
  pipeline_tipo?: PipelineTipo;
  da_verificare?: boolean;
  discordanza_bc?: boolean;
  anticipi_in_ritardo?: boolean;
  q?: string;
}

export function useM3Pipeline(opts: UsePipelineOptions = {}) {
  const params = new URLSearchParams();
  for (const s of opts.stato ?? []) params.append("stato", s);
  if (opts.pipeline_tipo) params.set("pipeline_tipo", opts.pipeline_tipo);
  if (opts.da_verificare !== undefined) params.set("da_verificare", String(opts.da_verificare));
  if (opts.discordanza_bc !== undefined) params.set("discordanza_bc", String(opts.discordanza_bc));
  if (opts.anticipi_in_ritardo) params.set("anticipi_in_ritardo", "true");
  if (opts.q) params.set("q", opts.q);
  const qs = params.toString();
  return useQuery({
    queryKey: ["m3", "pipeline", opts],
    queryFn: () =>
      apiFetch<CarichiPipelineResponse>(`/api/v1/m3/carichi${qs ? "?" + qs : ""}`),
    staleTime: 30_000,
  });
}

/** Worklist «Lavoro» (vista primaria M3): lane aggregate per lifecycle. */
export function useLavoro() {
  return useQuery({
    queryKey: ["m3", "lavoro"],
    queryFn: () => apiFetch<LavoroResponse>("/api/v1/m3/lavoro"),
    staleTime: 30_000,
  });
}

/** Avvisi di ritiro tracciati da Aida (un record per cliente; solleciti = contatore). */
export function useAvvisi(stato?: "aperto" | "chiuso") {
  return useQuery({
    queryKey: ["m3", "avvisi", stato ?? "tutti"],
    queryFn: () =>
      apiFetch<AvvisiResponse>(`/api/v1/m3/avvisi${stato ? `?stato=${stato}` : ""}`),
    staleTime: 30_000,
  });
}

/** Aggiorna un avviso (associa cliente / chiudi / note / collega carico). */
export function useAggiornaAvviso() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AvvisoRitiroUpdate }) =>
      apiFetch(`/api/v1/m3/avvisi/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "avvisi"] }),
  });
}

/** Imposta la modalità ritiro di una commessa (parcheggia / attiva / programma). */
export function useM3SetModalitaRitiro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      ordineNumero,
      modalita_ritiro,
      nota,
    }: {
      ordineNumero: number;
      modalita_ritiro: "standard" | "a_chiamata" | "programmato";
      nota?: string | null;
    }) =>
      apiFetch(`/api/v1/m3/commessa/${ordineNumero}/ritiro`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ modalita_ritiro, nota }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "lavoro"] });
      qc.invalidateQueries({ queryKey: ["m3", "bozza"] });
    },
  });
}

/** Cambio stato pipeline di un Carico (v4). */
export function useM3CambiaStatoCarico(caricoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CambiaStatoCaricoBody) =>
      apiFetch<CaricoDettaglio>(`/api/v1/m3/carico/${caricoId}/stato`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "pipeline"] });
      qc.invalidateQueries({ queryKey: ["m3", "carico", caricoId] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

/** Annulla un carico (soft) — v4. */
export function useM3AnnullaCarico(caricoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AnnullaAnticipoBody) =>
      apiFetch<CaricoDettaglio>(`/api/v1/m3/carico/${caricoId}/annulla`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "pipeline"] });
      qc.invalidateQueries({ queryKey: ["m3", "carico", caricoId] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

/** Stato logistico per N commesse: usato dalla colonna 'Logistica' in M1. */
export function useM3StatoLogisticoPerOrdini(numeri: number[]) {
  return useQuery({
    queryKey: ["m3", "stato-logistico", numeri.slice().sort()],
    queryFn: () => {
      if (numeri.length === 0) return Promise.resolve({ items: [] });
      const params = new URLSearchParams();
      for (const n of numeri) params.append("numeri", String(n));
      return apiFetch<StatoLogisticoResponse>(
        `/api/v1/m3/pipeline-per-ordini?${params.toString()}`
      );
    },
    staleTime: 15_000,
  });
}

/** Crea una commessa logistica da M1 (Passa o Anticipa). */
export function useM3CreaCommessaDaM1() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreaCommessaLogisticaBody) =>
      apiFetch<CaricoDettaglio>("/api/v1/m3/pipeline", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "pipeline"] });
      qc.invalidateQueries({ queryKey: ["m3", "stato-logistico"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

/** Commesse pronte a magazzino non ancora in pipeline (tabella sopra il kanban). */
export function useM3ProneAMagazzino() {
  return useQuery({
    queryKey: ["m3", "pronte-a-magazzino"],
    queryFn: () =>
      apiFetch<PronteMagazzinoResponse>("/api/v1/m3/pronte-a-magazzino"),
    staleTime: 30_000,
  });
}

/** Checklist documenti richiesti per una commessa (calcolata da paese). */
export function useM3ChecklistDocumenti(commessaId: string | null) {
  return useQuery({
    queryKey: ["m3", "checklist-doc", commessaId],
    queryFn: () =>
      apiFetch<ChecklistDocumentiResponse>(
        `/api/v1/m3/pipeline/${commessaId}/checklist-documenti`
      ),
    enabled: !!commessaId,
    staleTime: 30_000,
  });
}

/** Commesse BC aperte (qty_residua > 0) per un cliente — usato dal dropdown
 * "Associa commessa BC" quando una commessa pipeline non ha numero_ordine. */
export function useM3CommesseAperteCliente(clienteCodice: number | null) {
  return useQuery({
    queryKey: ["m3", "commesse-aperte-cliente", clienteCodice],
    queryFn: () =>
      apiFetch<CommesseAperteResponse>(
        `/api/v1/m3/commesse-aperte-cliente/${clienteCodice}`
      ),
    enabled: clienteCodice !== null && clienteCodice > 0,
    staleTime: 60_000,
  });
}

/** CRUD Carico aggregato. */
export function useM3Carico(caricoId: string | null) {
  return useQuery({
    queryKey: ["m3", "carico", caricoId],
    queryFn: () => apiFetch<CaricoDettaglio>(`/api/v1/m3/carico/${caricoId}`),
    enabled: !!caricoId,
    staleTime: 30_000,
  });
}

export function useM3CreaCarico() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreaCaricoBody) =>
      apiFetch<CaricoDettaglio>("/api/v1/m3/carico", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3"] }),
  });
}

export function useM3ImportFoglio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<ImportFoglioResponse>("/api/v1/m3/admin/import-foglio", {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "pipeline"] }),
  });
}

// ── B4: Proposta di carico ─────────────────────────────────────────

export function useM3Proponi() {
  return useMutation({
    mutationFn: (capacita?: number) =>
      apiFetch<ProponiCarichiResponse>(
        `/api/v1/m3/carichi/proponi${capacita ? `?capacita=${capacita}` : ""}`,
        { method: "POST" }
      ),
  });
}

export function useM3Applica() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ApplicaProposteBody) =>
      apiFetch<ApplicaProposteResponse>("/api/v1/m3/carichi/applica", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "pipeline"] }),
  });
}

// ── B5: Documenti ──────────────────────────────────────────────────

export function useM3DocumentiCarico(caricoId: string | null) {
  return useQuery({
    queryKey: ["m3", "documenti", caricoId],
    queryFn: () =>
      apiFetch<FascicoloFile[]>(`/api/v1/m3/carico/${caricoId}/documenti`),
    enabled: !!caricoId,
    staleTime: 30_000,
  });
}

export function useM3GeneraDocumenti(caricoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GeneraDocumentiBody) =>
      apiFetch<FascicoloFile[]>(
        `/api/v1/m3/carico/${caricoId}/genera-documenti`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "documenti", caricoId] });
      qc.invalidateQueries({ queryKey: ["m3", "checklist-doc", caricoId] });
    },
  });
}

export function useM3MarcaFirmato() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentoId, firmato }: { documentoId: string; firmato: boolean }) =>
      apiFetch<FascicoloFile>(`/api/v1/m3/documenti/${documentoId}/firma`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ firmato }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "documenti"] }),
  });
}

// ── Agente Documenti («Aida») ──────────────────────────────────────

export function useM3EmailAgente(stato?: string) {
  return useQuery({
    queryKey: ["m3", "agente-emails", stato ?? "tutte"],
    queryFn: () =>
      apiFetch<EmailInbound[]>(
        `/api/v1/m3/agente/emails${stato ? `?stato=${stato}` : ""}`
      ),
    staleTime: 15_000,
  });
}

export function useM3AgganciaEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ emailId, caricoId }: { emailId: string; caricoId: string }) =>
      apiFetch<EmailInbound>(`/api/v1/m3/agente/emails/${emailId}/aggancia`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ carico_id: caricoId }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "agente-emails"] });
      qc.invalidateQueries({ queryKey: ["m3", "pipeline"] });
    },
  });
}

export function useM3ScartaEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (emailId: string) =>
      apiFetch<EmailInbound>(`/api/v1/m3/agente/emails/${emailId}/scarta`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "agente-emails"] }),
  });
}

export function useM3CambiaStatoDocumenti(caricoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (stato: StatoDocumenti | null) =>
      apiFetch<CaricoDettaglio>(`/api/v1/m3/carico/${caricoId}/stato-documenti`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stato }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "pipeline"] });
      qc.invalidateQueries({ queryKey: ["m3", "carico", caricoId] });
    },
  });
}

export function useM3Conversazione(caricoId: string | null) {
  return useQuery({
    queryKey: ["m3", "conversazione", caricoId],
    queryFn: () =>
      apiFetch<ConversazioneCarico>(`/api/v1/m3/carico/${caricoId}/conversazione`),
    enabled: !!caricoId,
    staleTime: 15_000,
  });
}

export function useM3SetDocumenti() {
  return useQuery({
    queryKey: ["m3", "set-documenti"],
    queryFn: () => apiFetch<SetDocumentiRow[]>("/api/v1/m3/admin/set-documenti"),
    staleTime: 60_000,
  });
}

export function useM3UpsertSetDocumenti() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id?: string;
      body: Partial<SetDocumentiRow>;
    }) =>
      apiFetch<SetDocumentiRow>(
        id ? `/api/v1/m3/admin/set-documenti/${id}` : "/api/v1/m3/admin/set-documenti",
        {
          method: id ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "set-documenti"] });
      qc.invalidateQueries({ queryKey: ["m3", "checklist-doc"] });
    },
  });
}

export function useM3EliminaSetDocumenti() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/v1/m3/admin/set-documenti/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "set-documenti"] }),
  });
}

export function useM3CreaMemoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      scope: string;
      scope_ref?: string | null;
      chiave: string;
      contenuto: string;
      attiva?: boolean;
    }) =>
      apiFetch<AgenteMemoria>("/api/v1/m3/admin/agente/memorie", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "agente-memorie"] }),
  });
}

export function useM3MemorieAgente() {
  return useQuery({
    queryKey: ["m3", "agente-memorie"],
    queryFn: () => apiFetch<AgenteMemoria[]>("/api/v1/m3/admin/agente/memorie"),
    staleTime: 30_000,
  });
}

export function useM3ToggleMemoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memoriaId, attiva }: { memoriaId: string; attiva: boolean }) =>
      apiFetch<AgenteMemoria>(`/api/v1/m3/admin/agente/memorie/${memoriaId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attiva }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "agente-memorie"] }),
  });
}

export function useM3Whitelist() {
  return useQuery({
    queryKey: ["m3", "agente-whitelist"],
    queryFn: () =>
      apiFetch<AgenteWhitelistRow[]>("/api/v1/m3/admin/agente/whitelist"),
    staleTime: 60_000,
  });
}

export function useM3UpsertWhitelist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; descrizione?: string; attivo?: boolean }) =>
      apiFetch<AgenteWhitelistRow>("/api/v1/m3/admin/agente/whitelist", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "agente-whitelist"] }),
  });
}

export function useM3ImpostaAssegnazioni(caricoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AssegnazioniEditBody) =>
      apiFetch<CaricoDettaglio>(`/api/v1/m3/carico/${caricoId}/assegnazioni`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "carico", caricoId] });
      qc.invalidateQueries({ queryKey: ["m3", "pipeline"] });
    },
  });
}

// ── B6: Impostazioni ───────────────────────────────────────────────

export function useM3Impostazioni() {
  return useQuery({
    queryKey: ["m3", "impostazioni"],
    queryFn: () => apiFetch<ImpostazioniGlobali>("/api/v1/m3/admin/impostazioni"),
    staleTime: 60_000,
  });
}

export function useM3AggiornaImpostazioni() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ImpostazioniGlobaliUpdate) =>
      apiFetch<ImpostazioniGlobali>("/api/v1/m3/admin/impostazioni", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "impostazioni"] }),
  });
}

export function useM3PreferenzeCliente() {
  return useQuery({
    queryKey: ["m3", "preferenze-cliente"],
    queryFn: () =>
      apiFetch<PreferenzaCliente[]>("/api/v1/m3/admin/preferenze-cliente"),
    staleTime: 60_000,
  });
}

export function useM3UpsertPreferenza() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      clienteCodice,
      body,
    }: {
      clienteCodice: number;
      body: PreferenzaClienteUpsert;
    }) =>
      apiFetch<PreferenzaCliente>(
        `/api/v1/m3/admin/preferenze-cliente/${clienteCodice}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "preferenze-cliente"] }),
  });
}

export function useM3EliminaPreferenza() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clienteCodice: number) =>
      apiFetch<void>(`/api/v1/m3/admin/preferenze-cliente/${clienteCodice}`, {
        method: "DELETE",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "preferenze-cliente"] }),
  });
}

// ── Paesi → Regime (tab «Paesi & Regimi») ──────────────────────────

export function useM3Paesi() {
  return useQuery({
    queryKey: ["m3", "paesi"],
    queryFn: () => apiFetch<Paese[]>("/api/v1/m3/admin/paesi"),
    staleTime: 60_000,
  });
}

export function useM3UpsertPaese() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sigla, body }: { sigla: string; body: PaeseUpsert }) =>
      apiFetch<Paese>(`/api/v1/m3/admin/paesi/${encodeURIComponent(sigla)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "paesi"] });
      qc.invalidateQueries({ queryKey: ["m3", "proposte"] });
    },
  });
}

// ── SPEC-B: registro Marina (raggruppamenti, associazione, uscite pallet) ──

/** Proposte di raggruppamento/creazione carico da decidere (drawer «Rivedi proposte»). */
export function useM3Raggruppamenti() {
  return useQuery({
    queryKey: ["m3", "raggruppamenti"],
    queryFn: () => apiFetch<RaggruppamentoProposto[]>("/api/v1/m3/raggruppamenti"),
    staleTime: 30_000,
  });
}

export function useM3ConfermaRaggruppamento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<CaricoDettaglio>(`/api/v1/m3/raggruppamenti/${id}/conferma`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "raggruppamenti"] });
      qc.invalidateQueries({ queryKey: ["m3", "pipeline"] });
    },
  });
}

export function useM3RifiutaRaggruppamento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/v1/m3/raggruppamenti/${id}/rifiuta`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m3", "raggruppamenti"] }),
  });
}

/** Associa un ordine BC a un carico (AssociaCommessaSection). */
export function useM3AggiungiOrdineCarico(caricoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ordine_numero: number) =>
      apiFetch<CaricoDettaglio>(`/api/v1/m3/carico/${caricoId}/aggiungi-ordine`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ordine_numero }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "pipeline"] });
      qc.invalidateQueries({ queryKey: ["m3", "carico", caricoId] });
      qc.invalidateQueries({ queryKey: ["m3", "commesse-aperte"] });
    },
  });
}

/** Report «DATI ENZO»: pallet usciti per giorno (SPEC-B2). */
export function useM3UscitePallet(dal?: string, al?: string) {
  const p = new URLSearchParams();
  if (dal) p.set("dal", dal);
  if (al) p.set("al", al);
  const qs = p.toString();
  return useQuery({
    queryKey: ["m3", "uscite-pallet", dal, al],
    queryFn: () =>
      apiFetch<UscitePalletResponse>(`/api/v1/m3/report/uscite-pallet${qs ? "?" + qs : ""}`),
    staleTime: 60_000,
  });
}

// ── SPEC-C: preparazione carichi (bozza persistita) ────────────────

const BOZZA_KEY = ["m3", "bozza"];

export function useBozza() {
  return useQuery({
    queryKey: BOZZA_KEY,
    queryFn: () => apiFetch<Bozza>("/api/v1/m3/preparazione"),
    staleTime: 10_000,
  });
}

/** Helper: hook di mutazione che POSTa e aggiorna la bozza in cache (anima la board). */
function useBozzaMut<TArg>(fn: (arg: TArg) => Promise<Bozza>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (bozza) => {
      qc.setQueryData(BOZZA_KEY, bozza);
      qc.invalidateQueries({ queryKey: ["m3", "pipeline"] });
    },
  });
}

export function useProponiBozza() {
  return useBozzaMut<{ finestra?: number; parziali?: boolean } | void>((opts) => {
    const p = new URLSearchParams();
    if (opts && opts.finestra) p.set("finestra", String(opts.finestra));
    if (opts && opts.parziali === false) p.set("parziali", "false");
    const qs = p.toString();
    return apiFetch<Bozza>(`/api/v1/m3/preparazione/proponi${qs ? "?" + qs : ""}`, {
      method: "POST",
    });
  });
}

export function useSpostaPallet() {
  return useBozzaMut<{ da_carico: string; a_carico: string; articolo: string; qty_pallet: number }>(
    (b) =>
      apiFetch<Bozza>("/api/v1/m3/preparazione/sposta", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(b),
      }),
  );
}

export function useTogliPallet() {
  return useBozzaMut<{ carico: string; articolo: string; qty_pallet: number }>((b) =>
    apiFetch<Bozza>("/api/v1/m3/preparazione/togli", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(b),
    }),
  );
}

export function useAggiungiPallet() {
  return useBozzaMut<{ carico: string; articolo: string; qty_pallet: number }>((b) =>
    apiFetch<Bozza>("/api/v1/m3/preparazione/aggiungi", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(b),
    }),
  );
}

/** Annulla un residuo dalla proposta (decisione di Marina; annullamento vero su
 * Cube → promemoria). Il `conferma_carico` di SPEC-C è rimosso: il carico reale
 * nasce dalla NDP via email (spec M3 2026-06-19). */
export function useAnnullaResiduoBozza() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<AnnullaResiduoResponse>(
        `/api/v1/m3/preparazione/carico/${id}/annulla-residuo`,
        { method: "POST" },
      ),
    onSuccess: (res) => {
      qc.setQueryData(BOZZA_KEY, res.bozza);
      qc.invalidateQueries({ queryKey: ["m3", "lavoro"] });
    },
  });
}

/** Parcheggia una COMMESSA (ordine): ritiro a chiamata permanente, esce dalla
 * proposta (intera, anche se su più camion). Lo sblocca solo una NDP. Pulsante
 * primario in UI. Aggiorna bozza + worklist + dashboard. */
export function useParcheggiaCommessaBozza() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ordine: number) =>
      apiFetch<Bozza>(`/api/v1/m3/preparazione/commessa/${ordine}/parcheggia`, { method: "POST" }),
    onSuccess: (bozza) => {
      qc.setQueryData(BOZZA_KEY, bozza);
      qc.invalidateQueries({ queryKey: ["m3", "lavoro"] });
      qc.invalidateQueries({ queryKey: ["m3", "dashboard-logistica"] });
    },
  });
}

/** Invia la proposta al cliente: promuove la bozza-carico a Carico reale PROPOSTO
 * (impegna la merce, esce dalla proposta, entra nel Lavoro). Endpoint v5. */
export function useInviaProposta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (propostaCaricoId: string) =>
      apiFetch<Bozza>(`/api/v1/m3/preparazione/carico/${propostaCaricoId}/promuovi`, {
        method: "POST",
      }),
    onSuccess: (bozza) => {
      qc.setQueryData(BOZZA_KEY, bozza);
      qc.invalidateQueries({ queryKey: ["m3", "lavoro"] });
      qc.invalidateQueries({ queryKey: ["m3", "carichi"] });
      qc.invalidateQueries({ queryKey: ["m3", "dashboard-logistica"] });
    },
  });
}

/** Replace delle assegnazioni di un carico-bozza (drag/stepper manuale persistito). */
export function useImpostaAssegnazioniBozza() {
  return useBozzaMut<{ caricoId: string; assegnazioni: unknown[] }>((b) =>
    apiFetch<Bozza>(`/api/v1/m3/preparazione/carico/${b.caricoId}/assegnazioni`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ assegnazioni: b.assegnazioni }),
    }),
  );
}

// ── Sezione Documenti (redesign M3 #29) ──────────────────────────────

const DOCUMENTI_KEY = ["m3", "documenti-pacchetti"];

/** Storico dei pacchetti documenti (filtro stato + testuale opzionale). */
export function useM3DocumentiPacchetti(
  filtro: FiltroPacchetti = "aperti",
  q?: string,
) {
  const qs = new URLSearchParams({ stato: filtro });
  if (q) qs.set("q", q);
  return useQuery({
    queryKey: [...DOCUMENTI_KEY, filtro, q ?? ""],
    queryFn: () =>
      apiFetch<PacchettiResponse>(`/api/v1/m3/documenti/pacchetti?${qs.toString()}`),
    staleTime: 15_000,
  });
}

/** Resoconto di un singolo pacchetto. */
export function useM3DocumentiPacchetto(caricoId: string | null) {
  return useQuery({
    queryKey: ["m3", "documenti-pacchetto", caricoId],
    queryFn: () => apiFetch<PaccoDetail>(`/api/v1/m3/documenti/pacchetti/${caricoId}`),
    enabled: !!caricoId,
    staleTime: 15_000,
  });
}

/** KPI della sezione Documenti. */
export function useM3DocumentiKpi() {
  return useQuery({
    queryKey: ["m3", "documenti-kpi"],
    queryFn: () => apiFetch<DocumentiKpiResponse>("/api/v1/m3/documenti/kpi"),
    staleTime: 30_000,
  });
}

/** Upload di una NDP/DDT nel canale «trascinamento» → pipeline agente. */
export function useM3DocumentiUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, caricoId }: { file: File; caricoId?: string }) => {
      const fd = new FormData();
      fd.append("file", file);
      if (caricoId) fd.append("carico_id", caricoId);
      return apiUploadFile<DocumentiUploadResponse>("/api/v1/m3/documenti/upload", fd);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DOCUMENTI_KEY });
      qc.invalidateQueries({ queryKey: ["m3", "documenti-kpi"] });
      // Una NDP chiude la riga del tabellone e un DDT la fa uscire: il documento
      // caricato qui cambia i carichi previsti, non solo la sezione Documenti.
      qc.invalidateQueries({ queryKey: ["m3", "carichi-previsti"] });
    },
  });
}

/** Campi completabili di un documento (generico per tipo). */
export function useM3DocCompletamento(caricoId: string | null, tipo: string | null) {
  return useQuery({
    queryKey: ["m3", "completamento", caricoId, tipo],
    queryFn: () =>
      apiFetch<DocCompletamento>(
        `/api/v1/m3/documenti/pacchetti/${caricoId}/completamento/${tipo}`,
      ),
    enabled: !!caricoId && !!tipo,
    staleTime: 5_000,
  });
}

/** Salva i completamenti manuali e rigenera il documento. */
export function useM3SalvaDocCompletamento(caricoId: string, tipo: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DocCompletamentoBody) =>
      apiFetch<DocCompletamento>(
        `/api/v1/m3/documenti/pacchetti/${caricoId}/completamento/${tipo}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "completamento", caricoId, tipo] });
      qc.invalidateQueries({ queryKey: ["m3", "documenti-pacchetto", caricoId] });
      qc.invalidateQueries({ queryKey: DOCUMENTI_KEY });
    },
  });
}

/** Disambiguazione: assegna un file caricato (in coda) a un pacchetto. */
export function useM3AssegnaUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fileId, caricoId }: { fileId: string; caricoId: string }) =>
      apiFetch<PaccoDetail>(`/api/v1/m3/documenti/upload/${fileId}/assegna`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pacchetto_id: caricoId }),
      }),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: DOCUMENTI_KEY });
      qc.invalidateQueries({ queryKey: ["m3", "documenti-pacchetto", p.carico_id] });
      qc.invalidateQueries({ queryKey: ["m3", "documenti-kpi"] });
    },
  });
}

/** Rigenera l'intero pacchetto (mantieni override coerenti | riparti da zero). */
export function useM3RigeneraPacchetto(caricoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { modalita: "mantieni" | "zero" }) =>
      apiFetch<RigeneraEsito>(`/api/v1/m3/documenti/pacchetti/${caricoId}/rigenera`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "documenti-pacchetto", caricoId] });
      qc.invalidateQueries({ queryKey: ["m3", "completamento", caricoId] });
      qc.invalidateQueries({ queryKey: DOCUMENTI_KEY });
    },
  });
}

/** Rigenera un singolo documento (mantieni-by-default; 409 su conflitto → forza). */
export function useM3RigeneraDocumento(caricoId: string, tipo: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ forza }: { forza?: boolean } = {}) =>
      apiFetch<RigeneraEsito>(
        `/api/v1/m3/documenti/pacchetti/${caricoId}/documenti/${tipo}/rigenera${forza ? "?forza=true" : ""}`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "completamento", caricoId, tipo] });
      qc.invalidateQueries({ queryKey: ["m3", "documenti-pacchetto", caricoId] });
      qc.invalidateQueries({ queryKey: DOCUMENTI_KEY });
    },
  });
}

/** Salva un override manuale a livello di pacchetto (chiave condivisa) + ricalcola. */
export function useM3SalvaOverride(caricoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { chiave_campo: string; contesto?: string | null; valore: string }) =>
      apiFetch<PaccoDetail>(`/api/v1/m3/documenti/pacchetti/${caricoId}/override`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m3", "documenti-pacchetto", caricoId] });
      qc.invalidateQueries({ queryKey: ["m3", "completamento", caricoId] });
      qc.invalidateQueries({ queryKey: DOCUMENTI_KEY });
    },
  });
}

/** Elimina un pacchetto (Carico + fascicolo, cascade). Irreversibile. */
export function useM3EliminaPacchetto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (caricoId: string) =>
      apiFetch<void>(`/api/v1/m3/documenti/pacchetti/${caricoId}`, { method: "DELETE" }),
    onSuccess: (_d, caricoId) => {
      qc.invalidateQueries({ queryKey: DOCUMENTI_KEY });
      qc.invalidateQueries({ queryKey: ["m3", "documenti-pacchetto", caricoId] });
      qc.invalidateQueries({ queryKey: ["m3", "documenti-kpi"] });
    },
  });
}

// ── Motore documenti: sezione Template in Impostazioni (SPEC v1) ─────

const TEMPLATE_KEY = ["m3", "template"] as const;

/** Tipi documento configurabili come template (plico generabile). */
export function useM3TemplateTipi() {
  return useQuery({
    queryKey: [...TEMPLATE_KEY, "tipi"],
    queryFn: () => apiFetch<TemplateTipo[]>("/api/v1/m3/template/tipi"),
    staleTime: 5 * 60_000,
  });
}

/** Elenco template (attivi per default; opzionalmente filtrati per paese). */
export function useM3Template(paese?: string | null, includiStorico = false) {
  const qs = new URLSearchParams();
  if (paese) qs.set("paese", paese);
  if (includiStorico) qs.set("includi_storico", "true");
  return useQuery({
    queryKey: [...TEMPLATE_KEY, "list", paese ?? null, includiStorico],
    queryFn: () =>
      apiFetch<TemplateRead[]>(`/api/v1/m3/template${qs.toString() ? `?${qs}` : ""}`),
    staleTime: 10_000,
  });
}

/** Dettaglio di un template (manifest + placeholder + diff). */
export function useM3TemplateDettaglio(id: string | null) {
  return useQuery({
    queryKey: [...TEMPLATE_KEY, "detail", id],
    queryFn: () => apiFetch<TemplateDetail>(`/api/v1/m3/template/${id}`),
    enabled: !!id,
    staleTime: 5_000,
  });
}

/** Storico versioni per (paese, tipo). */
export function useM3TemplateVersioni(tipo: string | null, paese?: string | null) {
  const qs = new URLSearchParams();
  if (tipo) qs.set("tipo", tipo);
  if (paese) qs.set("paese", paese);
  return useQuery({
    queryKey: [...TEMPLATE_KEY, "versioni", tipo, paese ?? null],
    queryFn: () => apiFetch<TemplateRead[]>(`/api/v1/m3/template/versioni?${qs}`),
    enabled: !!tipo,
    staleTime: 5_000,
  });
}

function invalidaTemplate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: TEMPLATE_KEY });
  // Cambiare i template cambia lo stato «manca template» dei pacchetti.
  qc.invalidateQueries({ queryKey: DOCUMENTI_KEY });
  qc.invalidateQueries({ queryKey: ["m3", "documenti-pacchetto"] });
}

/** Panoramica a slot dei template per un regime (lista attesa × caricati). */
export function useM3TemplatePanoramica(regime: string | null) {
  return useQuery({
    queryKey: [...TEMPLATE_KEY, "panoramica", regime],
    queryFn: () =>
      apiFetch<TemplatePanoramica>(
        `/api/v1/m3/template/panoramica?regime=${encodeURIComponent(regime ?? "")}`,
      ),
    enabled: !!regime,
    staleTime: 10_000,
  });
}

/** Carica/sostituisce il .docx di un template_word (nuova versione). Scope:
 * paese_iso (override) oppure regime (default del regime). */
export function useM3TemplateUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      file: File;
      tipo_documento: string;
      paese_iso?: string | null;
      regime?: string | null;
      descrizione?: string | null;
      note_versione?: string | null;
    }) => {
      const fd = new FormData();
      fd.append("file", args.file);
      fd.append("tipo_documento", args.tipo_documento);
      if (args.paese_iso) fd.append("paese_iso", args.paese_iso);
      if (args.regime) fd.append("regime", args.regime);
      if (args.descrizione) fd.append("descrizione", args.descrizione);
      if (args.note_versione) fd.append("note_versione", args.note_versione);
      return apiUploadFile<TemplateDetail>("/api/v1/m3/template/upload", fd);
    },
    onSuccess: () => invalidaTemplate(qc),
  });
}

/** Crea/aggiorna (nuova versione) un documento `generato`. */
export function useM3TemplateGenerato() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GeneratoBody) =>
      apiFetch<TemplateDetail>("/api/v1/m3/template/generato", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidaTemplate(qc),
  });
}

/** Salva il manifest (nuova versione, asset portato avanti). */
export function useM3TemplateManifest(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ManifestBody) =>
      apiFetch<TemplateDetail>(`/api/v1/m3/template/${id}/manifest`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidaTemplate(qc),
  });
}

/** Riattiva una versione storica. */
export function useM3TemplateAttiva() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<TemplateDetail>(`/api/v1/m3/template/${id}/attiva`, { method: "POST" }),
    onSuccess: () => invalidaTemplate(qc),
  });
}

// ── M3 «Carichi previsti» (fase pre-NDP) ────────────────────────────

const CP_KEY = ["m3", "carichi-previsti"] as const;

/** Lista carichi previsti + i conteggi della testata.
 *
 *  `stato` sono i cinque riquadri della testata; `tutti` non è un riquadro — è
 *  quello che usa la ricerca, perché include anche le righe già uscite. */
export function useCarichiPrevisti(
  stato:
    | "attivi"
    | "da_sollecitare"
    | "con_domande"
    | "da_confermare_uscita"
    | "usciti"
    | "tutti" = "attivi",
) {
  return useQuery({
    queryKey: [...CP_KEY, "list", stato],
    queryFn: () =>
      apiFetch<CaricoPrevistoLista>(`/api/v1/m3/carichi-previsti?stato=${stato}`),
    staleTime: 10_000,
  });
}

/** Dettaglio di un carico previsto (thread, composizione, mapping). */
export function useCaricoPrevisto(id: string | null) {
  return useQuery({
    queryKey: [...CP_KEY, "detail", id],
    queryFn: () => apiFetch<CaricoPrevistoDetail>(`/api/v1/m3/carichi-previsti/${id}`),
    enabled: !!id,
    staleTime: 5_000,
  });
}

function invalidaCp(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: CP_KEY });
}

/** Nota + conferma lettura Aida. */
export function useCpPatch(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CaricoPrevistoPatch) =>
      apiFetch<CaricoPrevistoDetail>(`/api/v1/m3/carichi-previsti/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidaCp(qc),
  });
}

/**
 * Le stesse due mutazioni, ma con l'id passato alla chiamata invece che al
 * montaggio: nel calendario la riga su cui si scrive la decide il trascinamento,
 * quindi non c'è un id noto quando si prepara la mutazione.
 */
export function useCpPatchRiga() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CaricoPrevistoPatch }) =>
      apiFetch<CaricoPrevistoDetail>(`/api/v1/m3/carichi-previsti/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidaCp(qc),
  });
}

export function useCpUscitaRiga() {
  const qc = useQueryClient();
  return useMutation({
    // `giorno` assente = uscita di oggi (dalla riga del tabellone); con la data
    // = «è uscito quel giorno», il gesto del calendario sul passato.
    mutationFn: ({ id, giorno }: { id: string; giorno?: string | null }) =>
      apiFetch<CaricoPrevistoDetail>(`/api/v1/m3/carichi-previsti/${id}/partenza`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uscito: true, ...(giorno ? { giorno } : {}) }),
      }),
    onSuccess: () => invalidaCp(qc),
  });
}

/** Segna il giorno di partenza, lo revoca (giorno=null) o conferma l'uscita. */
export function useCpPartenza(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { giorno?: string | null; uscito?: boolean }) =>
      apiFetch<CaricoPrevistoDetail>(`/api/v1/m3/carichi-previsti/${id}/partenza`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidaCp(qc),
  });
}

/**
 * Il calendario dei ritiri: le stesse righe del tabellone, disposte sui giorni.
 *
 * Condivide `CP_KEY` con il tabellone, quindi ogni scrittura (una data, una
 * fascia, un'uscita confermata) aggiorna entrambe le viste. È il motivo per cui
 * non possono divergere: non sono due copie, sono due letture della stessa cosa.
 */
export function useCalendario(da: string | null, giorni = 14) {
  const qs = new URLSearchParams({ giorni: String(giorni) });
  if (da) qs.set("da", da);
  return useQuery({
    queryKey: [...CP_KEY, "calendario", da ?? "oggi", giorni],
    queryFn: () => apiFetch<CalendarioResponse>(`/api/v1/m3/calendario?${qs}`),
    staleTime: 10_000,
  });
}

/** Il tabellone delle partenze di un giorno (default oggi). */
export function usePartenze(giorno?: string) {
  const qs = giorno ? `?giorno=${giorno}` : "";
  return useQuery({
    queryKey: [...CP_KEY, "partenze", giorno ?? "oggi"],
    queryFn: () => apiFetch<PartenzeGiorno>(`/api/v1/m3/partenze${qs}`),
    refetchInterval: 60_000, // la pagina resta aperta: si aggiorna da sola
  });
}

/** Aggancia riferimento cliente → commessa (persiste nel mapping). */
export function useCpAggancia(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AgganciaCommessaBody) =>
      apiFetch<CaricoPrevistoDetail>(`/api/v1/m3/carichi-previsti/${id}/aggancia`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidaCp(qc),
  });
}

/** Crea un SOLLECITO in Bacheca per Marina. */
export function useCpSollecito(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ bacheca_voce_id: string }>(
        `/api/v1/m3/carichi-previsti/${id}/sollecito`,
        { method: "POST" },
      ),
    onSuccess: () => invalidaCp(qc),
  });
}

// ── Associazioni email → cliente (tab Impostazioni) ───────────────────

const CLIENTE_EMAIL_KEY = ["m3", "cliente-email"];

export function useM3ClienteEmail() {
  return useQuery({
    queryKey: CLIENTE_EMAIL_KEY,
    queryFn: () => apiFetch<ClienteEmailResponse>("/api/v1/m3/cliente-email"),
    staleTime: 30_000,
  });
}

export function useM3RisolviPropostaEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PropostaResolve }) =>
      apiFetch<ClienteEmailResponse>(`/api/v1/m3/cliente-email/proposte/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: CLIENTE_EMAIL_KEY }),
  });
}

export function useM3UpsertClienteEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ email, body }: { email: string; body: ClienteEmailUpsert }) =>
      apiFetch<ClienteEmailResponse>(
        `/api/v1/m3/cliente-email/${encodeURIComponent(email)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: CLIENTE_EMAIL_KEY }),
  });
}

export function useM3EliminaClienteEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      apiFetch<ClienteEmailResponse>(
        `/api/v1/m3/cliente-email/${encodeURIComponent(email)}`,
        { method: "DELETE" },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: CLIENTE_EMAIL_KEY }),
  });
}
